import { createContext, useContext } from 'react'

/**
 * Die Gliederung schiebt sich zusammen (7.3 Abschnitt 13, R444–R447).
 *
 * **Zuklappen ist nicht Ausblenden.** Die Nullzeilenunterdrückung (R420) nimmt
 * eine Zeile *weg*; hier bleibt die Summenzeile stehen und nur ihre
 * Unterpositionen fahren ein. Das ist derselbe Unterschied wie zwischen einer
 * gestrichenen Position und einer, die man gerade nicht aufschlägt.
 *
 * **R419 bleibt unberührt:** Der Bericht liefert die ganze Gliederung, und die
 * Vorgabe am Bildschirm ist Vollständigkeit. Zuklappen ist eine Handlung des
 * Anwenders, kein Vorgabewert — deshalb steht es auch nicht bei den
 * Anzeigeoptionen, wo die Haken sitzen, die einer setzt und vergisst.
 *
 * **Die Ebene ist die ganze Auskunft.** Eine Berichtszeile kennt ihre Tiefe,
 * nicht ihre Kinder; die Zeilen stehen in Lesereihenfolge. Daraus folgt alles
 * Übrige: Kind ist, was danach tiefer steht, bis wieder etwas gleich tief
 * oder flacher kommt. Ein zweiter Baum neben dieser Reihenfolge liefe mit der
 * Zeit auseinander — derselbe Grund wie bei der Liste der Bildschirme, die am
 * 2026-09-10 auseinandergelaufen ist.
 */

/** Was eine Zeile beisteuern muss, damit die Gliederung mit ihr rechnen kann. */
export type Gliederungszeile = {
  position: string
  ebene: number
}

export type Gliederungszustand = {
  /** Die zugeklappten Positionen — aus der Adresse (R446). */
  zu: ReadonlySet<string>
  /** Eine Position auf- oder zuklappen; der Linksklick auf den Pfeil (R444). */
  umschalten: (position: string) => void
  /** Den ganzen Zustand setzen — was die Ebenenwahl im Menü braucht (R445). */
  setzen: (positionen: ReadonlySet<string>) => void
}

/**
 * Die Ausfallstellung: **alles offen**.
 *
 * Ein Bericht ohne Zusammenhang — im Test, im Nachweis, in einer Vorschau —
 * soll nichts vermissen lassen. Wer nichts zugeklappt hat, hat alles vor sich.
 */
export const OFFENE_GLIEDERUNG: Gliederungszustand = {
  zu: new Set<string>(),
  umschalten: () => {},
  setzen: () => {},
}

const Kontext = createContext<Gliederungszustand>(OFFENE_GLIEDERUNG)

export const Gliederungskontext = Kontext.Provider

export function useGliederung(): Gliederungszustand {
  return useContext(Kontext)
}

/**
 * Was eine Tabelle von der Gliederung braucht — an **einer** Stelle gerechnet.
 *
 * Bilanz, GuV, das Strukturbild, das Periodenbild und die drei Spiegel
 * zeichnen dieselbe Zeilenachse (R446). Jede Tabelle für sich rechnen zu
 * lassen hieße, fünf Fassungen derselben Entscheidung zu pflegen — und die
 * sechste zeichnete dann einen Pfeil, den die anderen nicht kennen.
 *
 * Übergeben werden die Zeilen, **nachdem** die Nullzeilenunterdrückung sie
 * gekürzt hat: Der Pfeil gehört an das, was die Tabelle wirklich zeigt.
 */
export function useGliederungsbild<T extends Gliederungszeile>(zeilen: readonly T[]) {
  const { zu, umschalten, setzen } = useGliederung()
  const klappbar = klappbarePositionen(zeilen)

  return {
    /** Die Zeilen, die zu zeichnen sind. */
    zeilen: ohneZugeklappte(zeilen, zu),
    /** Ob diese Position einen Pfeil trägt (R444). */
    klappbar: (position: string) => klappbar.has(position),
    /** Ob sie zugeklappt ist. */
    zugeklappt: (position: string) => zu.has(position),
    umschalten,
    /** Die Punkte für das Zeilenmenü (R445). */
    menue: {
      ebenen: ebenenwahl(zeilen),
      etwasZu: zeilen.some((z) => zu.has(z.position)),
      aufEbene: (ebene: number) => setzen(bisEbene(zeilen, ebene)),
      aufGanz: () => setzen(new Set<string>()),
    },
  }
}

/**
 * Die Positionen, die überhaupt einen Pfeil bekommen: die mit Unterpositionen.
 *
 * **Gerechnet wird auf den Zeilen, die die Tabelle zeichnet** — also nach der
 * Nullzeilenunterdrückung und vor dem Zuklappen. Sonst trüge eine Position
 * einen Pfeil, deren einzige Unterposition gerade ausgeblendet ist, und der
 * Pfeil klappte auf ein leeres Nichts auf.
 */
export function klappbarePositionen(zeilen: readonly Gliederungszeile[]): Set<string> {
  const raus = new Set<string>()

  for (let i = 0; i < zeilen.length - 1; i += 1) {
    const hier = zeilen[i]
    const naechste = zeilen[i + 1]
    if (hier && naechste && naechste.ebene > hier.ebene) {
      raus.add(hier.position)
    }
  }

  return raus
}

/**
 * Die Zeilen ohne das, was unter einer zugeklappten Position steht.
 *
 * **Die äußerste zugeklappte Position gewinnt** — derselbe Satz wie beim
 * Teilkonzern (R407): Ist A zu, ist alles darunter zu, gleich was jemand
 * vorher unter A aufgeklappt hatte. Sonst hinge eine Zeile in der Tabelle,
 * deren Mutter niemand sieht.
 */
export function ohneZugeklappte<T extends Gliederungszeile>(
  zeilen: readonly T[],
  zu: ReadonlySet<string>,
): T[] {
  if (zu.size === 0) {
    return [...zeilen]
  }

  const raus: T[] = []
  // Die Ebene der zugeklappten Position, unter der wir gerade stehen.
  let deckel: number | null = null

  for (const zeile of zeilen) {
    if (deckel !== null && zeile.ebene > deckel) {
      continue
    }

    deckel = null
    raus.push(zeile)

    if (zu.has(zeile.position)) {
      deckel = zeile.ebene
    }
  }

  return raus
}

/**
 * Die Ebenen, für die der Menüpunkt *Gliederung bis Ebene N zeigen* etwas
 * bewirkt (R445).
 *
 * Die oberste Ebene bleibt draußen: „bis zur Wurzel" ließe von der Bilanz
 * *Aktiva* und *Passiva* übrig — zwei Zahlen, für die niemand ein Menü
 * aufmacht. Die unterste ebenfalls: Sie zeigt, was ohnehin dasteht.
 */
export function ebenenwahl(zeilen: readonly Gliederungszeile[]): number[] {
  if (zeilen.length === 0) {
    return []
  }

  const ebenen = zeilen.map((z) => z.ebene)
  const kleinste = Math.min(...ebenen)
  const groesste = Math.max(...ebenen)
  const raus: number[] = []

  for (let e = kleinste + 1; e < groesste; e += 1) {
    raus.push(e)
  }

  return raus
}

/**
 * Was zuzuklappen ist, damit die Gliederung **bis** dieser Ebene steht.
 *
 * Es genügen die Zeilen genau auf der Ebene: Was tiefer liegt, verschwindet
 * mit ihnen, weil der Deckel in {@link ohneZugeklappte} bis zur nächsten
 * gleich tiefen Zeile trägt.
 */
export function bisEbene(zeilen: readonly Gliederungszeile[], ebene: number): Set<string> {
  const klappbar = klappbarePositionen(zeilen)
  return new Set(zeilen.filter((z) => z.ebene === ebene && klappbar.has(z.position)).map((z) => z.position))
}

/**
 * Die Positionen über einer Zeile — was aufgehen muss, damit sie zu sehen ist.
 *
 * **Der Suchtreffer holt seine Zeile hervor** (R177, R446): Ein Fund unter
 * einer zugeklappten Position wäre ein Fund, den niemand sieht. Gesucht wird
 * der ganze Bericht, nicht der Bildschirm.
 */
export function vorfahren(zeilen: readonly Gliederungszeile[], position: string): string[] {
  const stelle = zeilen.findIndex((z) => z.position === position)
  if (stelle < 0) {
    return []
  }

  const raus: string[] = []
  let tiefe = zeilen[stelle]?.ebene ?? 0

  for (let i = stelle - 1; i >= 0; i -= 1) {
    const zeile = zeilen[i]
    if (zeile && zeile.ebene < tiefe) {
      raus.unshift(zeile.position)
      tiefe = zeile.ebene
    }
  }

  return raus
}
